import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import TableData from '../../custom/Table/TableData';
import CellType from '../../../constants/type';
import useI18n from '../../../hooks/useI18n';
import Search from '../../custom/Search/Search';
import useUserInfo from '../../../hooks/useUserInfo';

import { updateBasketState } from '../../../state/updateBasketState';
import { useRecoilState } from "recoil";
import basketService from "../../../api/basket";
import orderService from "../../../api/order";

import { useParams, useLocation } from 'react-router-dom';
import ConfirmDialog from '../../custom/ConfirmDialog/ConfirmDialog';
import DetailScreen from '../../custom/DetailScreen/DetailScreen';

import { basketState } from '../../../state/basketState';
import { currencySignPrice1, appCompno, compDetailUrl } from '../../../config';

function Order() {
    const config = require('../../../config');
    const { lang, l } = useI18n();
    const { number } = useParams();
    const location = useLocation();

    const [orderData, setOrderData] = React.useState(null);
    const [orderItems, setOrderItems] = React.useState([]);
    const [totweight, setTotweight] = React.useState(0);
    const [totvolume, setTotvolume] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    
    const [search, setSearch] = React.useState([]);
    const [openDetail, setOpenDetail] = React.useState(false);
    const [detailUrl, setDetailUrl] = React.useState('');

    const [searchValue, setSearchValue] = React.useState('');

    // Пагінація для товарів замовлення
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(50);
    const [ownPagination, setOwnPagination] = React.useState({
        page: 1,
        pageSize: 50,
        totalItems: 0,
        totalPages: 0,
        rowsPerPageOptions: [20, 50, 100, 200],
    });

    const { user } = useUserInfo();

    const [updateBasket, setUpdateBasket] = useRecoilState(updateBasketState);
    const [, setBasketRecoil] = useRecoilState(basketState);

    const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
    const [selectedArticleCodes, setSelectedArticleCodes] = React.useState([]);


    React.useEffect(() => {
        if (!user) {
            return;
        }

        const fetchOrderDetails = async () => {
            setLoading(true);
            try {
                let recid = location.state?.recid;

                if (!recid) {
                    const ordersResponse = await orderService.findAllByUserId(user.CODE, lang, 1, 100);
                    const order = ordersResponse?.orders?.find((item) => item.OFFICIALSERNR === number);
                    if (order) {
                        recid = order.recid;
                        setOrderData(order);
                    }
                }

                if (recid) {
                    const response = await orderService.getOrderItems(recid, lang);
                    if (response) {
                        setOrderData(response.order);
                        setOrderItems(response.items || []);
                        setTotweight(response.totweight || 0);
                        setTotvolume(response.totvolume || 0);
                    }
                }
            } catch (error) {
                console.error('Error getOrderDetails :>> ', error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [lang, location.state?.recid, number, user]);

    React.useEffect(() => {
        setSelectedArticleCodes(orderItems.map((item) => item.ARTCODE).filter(Boolean));
    }, [orderItems]);

    const filteredRows = searchValue ? search : orderItems;

    React.useEffect(() => {
        const totalItems = filteredRows.length;
        setOwnPagination(prev => ({
            ...prev,
            page: page,
            pageSize: pageSize,
            totalItems: totalItems,
            totalPages: Math.ceil(totalItems / pageSize),
        }));
    }, [filteredRows.length, page, pageSize]);

    const filteredArticleCodes = filteredRows
        .map((item) => item.ARTCODE)
        .filter(Boolean);
    const isAllFilteredSelected = filteredArticleCodes.length > 0
        && filteredArticleCodes.every((code) => selectedArticleCodes.includes(code));
    const isSomeFilteredSelected = filteredArticleCodes.some((code) => selectedArticleCodes.includes(code));

    const columns = [
        {
            id: 'isSelected',
            label: l('Select all'),
            minWidth: 60,
            type: CellType.CHECKBOX,
            align: 'center',
            headerCheckboxProps: {
                checked: isAllFilteredSelected,
                indeterminate: isSomeFilteredSelected && !isAllFilteredSelected,
                disabled: filteredArticleCodes.length === 0,
                ariaLabel: l('Select all products'),
                onChange: (event) => {
                    const { checked } = event.target;

                    setSelectedArticleCodes((prev) => {
                        if (checked) {
                            return [...new Set([...prev, ...filteredArticleCodes])];
                        }

                        return prev.filter((code) => !filteredArticleCodes.includes(code));
                    });
                },
            },
        },
        { id: 'ARTCODE', label: l('Code'), minWidth: 150, type: CellType.STRING },
        { id: 'FNAME', label: l('Name'), minWidth: 200, type: CellType.PRODUCT_NAME },
        { id: 'FIMG', label: l('Image'), minWidth: 80, type: CellType.IMG, align: 'center' },
        { id: 'CALCPRICE', label: `${l('Price')}` + (appCompno === '2' ? `,${currencySignPrice1}` : ''), minWidth: 100, type: CellType.NUMBER, align: 'center' },
        { id: 'QUANT', label: l('Quantity'), minWidth: 80, type: CellType.NUMBER, align: 'center' },
        { id: 'FINSTOCK', label: l('Availability'), minWidth: 120, type: CellType.FINSTOCK, align: 'center' },
        { id: 'rdbSUM', label: l('Sum'), minWidth: 80, type: CellType.PRODUCT_SUM},
    ];

    const handelSearch = (value) => {

        if (value === '') {
            setSearchValue('');
            setSearch([]);
            return;
        }

        setSearchValue(value);

        setSearch(orderItems.filter((item) =>
            item.FNAME?.toLowerCase().includes(value.toLowerCase())
            || item.ARTCODE?.toLowerCase().includes(value.toLowerCase())
            || item.FNAMERUS?.toLowerCase().includes(value.toLowerCase())
            || item.SPEC?.toLowerCase().includes(value.toLowerCase())
        ));
    }

    function setTableRows() {
        return filteredRows;
    }

    // Пагіновані дані для відображення
    const getPaginatedData = () => {
        const data = setTableRows();
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return data.slice(startIndex, endIndex);
    };

    const handleChangePage = (event, value) => {
        setPage(value);
    };

    const handleChangeRowsPerPage = (event) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setPage(1);
    };

    const copyOrderToBasket = async (isClear = false) => {
        try {
            await basketService.copyOrderToBasket({
                orderNumber: orderData?.SERNR,
                isClear,
                checkedList: selectedArticleCodes,
            })
            setUpdateBasket({BoolUpdate: !updateBasket.BoolUpdate});
        } catch (error) {
            console.error('Error copyOrderToBasket :>> ', error);
        }

        try {
            const basket = await basketService.findAllByUserId(user.CODE, lang);
            setBasketRecoil({ basket });
        } catch (error) {
            console.error('Error handleAddToBasket :>> ', error);
        }
    }


    const handleAddToBasket = () => {
        copyOrderToBasket();
    }

    const handleRewriteBasket = () => {
        copyOrderToBasket(true);
        setOpenConfirmDialog(false)
    }

     const handelAction = (event, type, row) => {
            switch (type) {
                case CellType.CHECKBOX:
                    setSelectedArticleCodes((prev) => {
                        if (event.target.checked) {
                            return prev.includes(row.ARTCODE) ? prev : [...prev, row.ARTCODE];
                        }

                        return prev.filter((code) => code !== row.ARTCODE);
                    });
                    break;
                case CellType.PRODUCT_NAME:
                    const url = compDetailUrl + row.FINFOPAGE;
                    setOpenDetail(true);
                    setDetailUrl(url);
                    break;
    
                default:
                    break;
            }
        };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box display="flex" justifyContent="space-between" margin={'10px 0'} >
                <Box display="flex" flexGrow={1} justifyContent='space-between' >
                    <Box>
                        <Typography component="h2" variant="h5">
                            {l('Order')}: {number}
                        </Typography>
                    </Box>
                    <Box display='flex' gap={2}>
                        <Button
                            variant="contained"
                            autoFocus
                            color="success"
                            onClick={handleAddToBasket}
                        >
                            {l('Add to basket')}
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => setOpenConfirmDialog(true)}
                        >
                            {l('Rewrite basket')}
                        </Button>
                    </Box>
                    <Box>
                        <Typography display='flex' component="p" variant="p">
                            {l('Order amount')}: {orderData?.SUM4}{config.currencySignPrice1}
                        </Typography>
                        <Box display="flex">
                            <Typography color="#dc3545" marginRight={1} component="p" variant="p">
                                {totweight} {l('Kg')}
                            </Typography>
                            <Typography color="#007bff" component="p" variant="p">
                                {totvolume} {l('m³')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Search onChange={handelSearch} value={searchValue} />
            </Box>
            <Box display="flex">
                <Box width="100%">
                    <TableData
                        columns={columns}
                        cellBtnActionByType={handelAction}
                        rows={getPaginatedData().map((item) => ({
                            ...item,
                            isSelected: selectedArticleCodes.includes(item.ARTCODE),
                        }))}
                        tableEmptyText={l('No data available')}
                        ownPagination={ownPagination}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            </Box>
            <ConfirmDialog 
            handleConfirm={handleRewriteBasket} 
            open={openConfirmDialog} 
            handleClose={() => setOpenConfirmDialog(false)} 
            title={`⚠️ ${l('Rewrite basket')}`}
            contentText={`${l('Are you sure that you want to rewrite the basket, all other products that were selected will be deleted')}!`}
            denyButtonText={l('Cancel')}
            confirmButtonText={l('Agree')}
            />
            <DetailScreen isOpened={openDetail} setOpenDetail={setOpenDetail} detailUrl={detailUrl} />
        </>
    );
}

export default Order;